// PlannerTaskDialog — add / edit a planner task with the exact fields the spec
// asks for, driving the raw-write planner endpoints so all 5 statuses persist.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Paperclip } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { workPlannerApi, PLANNER_STATUSES, PLANNER_PRIORITIES, normalizePlannerStatus } from "@/services/workPlannerApi";

const dateInput = (v) => { if (!v) return ""; const d = new Date(v); return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10); };

export default function PlannerTaskDialog({ open, onClose, onSaved, onDeleted, clientId, clientName, members = [], task, defaultDate, canDelete = true }) {
  const editing = !!task;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        title: task.title || "", description: task.description || "",
        assigned_to: (task.assigned_users || [])[0] || "",
        priority: task.priority || "medium",
        status: normalizePlannerStatus(task.status),
        start_date: dateInput(task.start_date), due_date: dateInput(task.due_date || task.scheduled_date),
        notes: task.notes || "",
      });
    } else {
      setForm({ title: "", description: "", assigned_to: "", priority: "medium", status: "pending", start_date: dateInput(defaultDate), due_date: dateInput(defaultDate), notes: "" });
    }
  }, [open, task, defaultDate]);

  if (!open || !form) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Task name is required");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(), description: form.description,
        assigned_users: form.assigned_to ? [form.assigned_to] : [],
        priority: form.priority, status: form.status,
        start_date: form.start_date || null, due_date: form.due_date || form.start_date || null,
        notes: form.notes,
      };
      if (editing) await workPlannerApi.updateTask(task.task_id, payload);
      else await workPlannerApi.createTask({ ...payload, client_id: clientId, date: form.due_date || form.start_date || null });
      toast.success(editing ? "Task updated" : "Task added");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this task? This cannot be undone.")) return;
    setDeleting(true);
    try { await workPlannerApi.deleteTask(task.task_id); toast.success("Task deleted"); onDeleted?.(); onClose(); }
    catch (e) { toast.error(e.message); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader><DialogTitle>{editing ? "Edit task" : "Add task"}{clientName ? ` · ${clientName}` : ""}</DialogTitle></DialogHeader>
        <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
          <div>
            <label className="text-xs font-medium text-slate-600">Task name *</label>
            <input value={form.title} onChange={(e) => set({ title: e.target.value })} autoFocus data-testid="planner-dialog-title"
              placeholder="e.g. 12 Social Media Posts, Weekly SEO, Monthly Report" className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Description</label>
            <textarea value={form.description} onChange={(e) => set({ description: e.target.value })} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Assign to</label>
              <select value={form.assigned_to} onChange={(e) => set({ assigned_to: e.target.value })} data-testid="planner-dialog-assign" className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">
                <option value="">Unassigned</option>
                {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Priority</label>
              <select value={form.priority} onChange={(e) => set({ priority: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">
                {PLANNER_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Status</label>
              <select value={form.status} onChange={(e) => set({ status: e.target.value })} data-testid="planner-dialog-status" className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm">
                {PLANNER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Start date</label>
              <input type="date" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Due date</label>
              <input type="date" value={form.due_date} onChange={(e) => set({ due_date: e.target.value })} data-testid="planner-dialog-due" className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Notes</label>
            <textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400">
            <Paperclip className="h-3.5 w-3.5" /> Attachments — coming soon
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div>
            {editing && canDelete && (
              <Button variant="outline" onClick={remove} disabled={deleting} className="rounded-lg border-rose-200 text-rose-600 hover:bg-rose-50">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="planner-dialog-save" className="rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Add task"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
